import React, { useState, useEffect } from 'react';
import { notifyCartUpdated } from '../utils/cartEvents';
import { useHistory } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Upload, AlertCircle, X, Download, ShoppingCart, 
  Loader, FileText, Filter, Package, DollarSign,
  MessageSquare, RefreshCw, CheckCircle, AlertTriangle, XCircle
} from 'lucide-react';
import { supabase } from '../supabaseClient';
import * as XLSX from 'xlsx';
import { showNotification, showError } from '../utils/notificationHandler';
import { formatCurrency } from '../utils/currency';
import { useOrderingEnabled } from '../hooks/useOrderingEnabled';
import PageHeader from './shared/PageHeader';
import Card from './shared/Card';
import Button from './shared/Button';
import EmptyState from './shared/EmptyState';
import StatusBadge from './shared/StatusBadge';

const LoadingOverlay = ({ message }) => (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div className="bg-white p-6 rounded-lg shadow-xl flex flex-col items-center">
      <Loader className="w-12 h-12 text-brand animate-spin" />
      <p className="mt-4 text-lg font-semibold text-gray-900">{message}</p>
    </div>
  </div>
);

const STOCK_STATUS = {
  'in-stock': { tone: 'success', icon: CheckCircle, label: 'In Stock' },
  'low-stock': { tone: 'warning', icon: AlertTriangle, label: 'Low Stock' },
  'out-of-stock': { tone: 'danger', icon: XCircle, label: 'Out of Stock' },
  'not-found': { tone: 'neutral', icon: XCircle, label: 'Not Found' },
};

// Icon chip colours for the summary tiles, matching the StatusBadge tones above.
const TILE_CHIP = {
  neutral: 'bg-gray-100 text-gray-500',
  success: 'bg-green-100 text-green-700',
  warning: 'bg-orange-100 text-orange-700',
  danger: 'bg-red-100 text-red-700',
};

const BulkQuery = () => {
  const { enabled: orderingEnabled, message: orderingMessage } = useOrderingEnabled();
  const [file, setFile] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [loading, setLoading] = useState(false);
  const [uploadedData, setUploadedData] = useState([]);
  const [isReviewing, setIsReviewing] = useState(false);
  const [accountId, setAccountId] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all'); // all, in-stock, low-stock, out-of-stock, not-found
  const history = useHistory();

  const placeholderImageUrl = "https://syd1.digitaloceanspaces.com/cdn-assets.mhs/images/placeholders/mhs_placeholder.png";

  useEffect(() => {
    const fetchAccountId = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data, error } = await supabase
          .from('accounts')
          .select('id')
          .eq('id', user.id)
          .single();

        if (error) {
          console.error('Error fetching account ID:', error);
        } else {
          setAccountId(data.id);
        }
      }
    };

    fetchAccountId();
  }, []);

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  };

  const handleFile = (file) => {
    if (file.type !== 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' &&
        file.type !== 'application/vnd.ms-excel' &&
        file.type !== 'text/csv') {
      showNotification('Please upload a valid Excel or CSV file', 'error');
      return;
    }
    setFile(file);
  };

  const handleUpload = async () => {
    if (!file) {
      showNotification('Please select a file to upload', 'error');
      return;
    }

    setLoading(true);

    try {
      const data = await readFileAsync(file);
      const queries = parseFileData(data);
      const enrichedQueries = await enrichQueryData(queries);
      setUploadedData(enrichedQueries);
      setIsReviewing(true);
      showNotification('File processed successfully. Review your query results.', 'success');
    } catch (error) {
      console.error('Error processing file:', error);
      showError(error, 'Failed to process file. Please check the file and try again.');
    } finally {
      setLoading(false);
    }
  };

  const readFileAsync = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const data = e.target.result;
        resolve(data);
      };
      reader.onerror = (error) => reject(error);
      reader.readAsBinaryString(file);
    });
  };

  const parseFileData = (data) => {
    const workbook = XLSX.read(data, { type: 'binary' });
    const sheetName = workbook.SheetNames[0];
    const sheet = workbook.Sheets[sheetName];
    return XLSX.utils.sheet_to_json(sheet, { defval: null });
  };

  const enrichQueryData = async (queries) => {
    const enrichedQueries = [];
    setIsSearching(true);
    
    for (const query of queries) {
      const partNumber = query.PartNumber || query.CustomerMaterialNumber;
      
      if (!partNumber) {
        console.error('Skipping row without PartNumber or CustomerMaterialNumber');
        continue;
      }
      
      try {
        const response = await fetch('/api/enquire/', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            partNumber: partNumber,
            account_id: accountId
          }),
        });
  
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
  
        const data = await response.json();
  
        if (data.cross_references && data.cross_references.length > 0) {
          const primaryPart = data.cross_references[0];
          const quantity = query.Quantity || 1;
          const unitPrice = primaryPart.buy_price || primaryPart.base_price || 0;
          const available = primaryPart.qty_available || 0;
          
          enrichedQueries.push({
            PartNumber: partNumber,
            PrimaryPartNumber: primaryPart.prod_code,
            Description: primaryPart.main_description,
            Quantity: quantity,
            UnitPrice: unitPrice,
            ExtendedPrice: unitPrice * quantity,
            QuantityAvailable: available,
            SmallImageUrl: primaryPart.small_image_url || placeholderImageUrl,
            isAlternative: true,
            isFound: true,
            stockStatus: getStockStatus(available, quantity)
          });
        } else if (data.prod_code || data.bom_code) {
          const quantity = query.Quantity || 1;
          const unitPrice = data.buy_price || data.base_price || 0;
          const available = data.qty_available || 0;
          
          enrichedQueries.push({
            PartNumber: partNumber,
            Description: data.Description || 'N/A',
            Quantity: quantity,
            UnitPrice: unitPrice,
            ExtendedPrice: unitPrice * quantity,
            QuantityAvailable: available,
            SmallImageUrl: data.small_image_url || placeholderImageUrl,
            isAlternative: false,
            isFound: true,
            stockStatus: getStockStatus(available, quantity)
          });
        } else {
          throw new Error('Unexpected API response structure');
        }
      } catch (error) {
        console.error(`Error fetching details for ${partNumber}:`, error);
        enrichedQueries.push({
          PartNumber: partNumber,
          Description: 'Part number not found',
          Quantity: query.Quantity || 1,
          UnitPrice: 0,
          ExtendedPrice: 0,
          QuantityAvailable: 0,
          SmallImageUrl: placeholderImageUrl,
          isAlternative: false,
          isFound: false,
          stockStatus: 'not-found'
        });
      }
    }
    
    setIsSearching(false);
    return enrichedQueries;
  };

  const getStockStatus = (available, requested) => {
    if (available === 0) return 'out-of-stock';
    if (available < requested) return 'low-stock';
    return 'in-stock';
  };

  const getStatusBadge = (status) => {
    const config = STOCK_STATUS[status];
    if (!config) return null;
    const Icon = config.icon;
    return (
      <StatusBadge tone={config.tone} icon={<Icon className="w-3 h-3" aria-hidden="true" />}>
        {config.label}
      </StatusBadge>
    );
  };

  const calculateSummary = () => {
    const total = uploadedData.length;
    const inStock = uploadedData.filter(item => item.stockStatus === 'in-stock').length;
    const lowStock = uploadedData.filter(item => item.stockStatus === 'low-stock').length;
    const outOfStock = uploadedData.filter(item => item.stockStatus === 'out-of-stock').length;
    const notFound = uploadedData.filter(item => item.stockStatus === 'not-found').length;
    const totalValue = uploadedData.reduce((sum, item) => sum + (item.ExtendedPrice || 0), 0);
    
    return { total, inStock, lowStock, outOfStock, notFound, totalValue };
  };

  const getFilteredData = () => {
    if (filterStatus === 'all') return uploadedData;
    return uploadedData.filter(item => item.stockStatus === filterStatus);
  };

  const handleExportResults = () => {
    const exportData = uploadedData.map(item => ({
      'Part Number': item.PartNumber,
      'Primary Part Number': item.PrimaryPartNumber || item.PartNumber,
      'Description': item.Description,
      'Quantity': item.Quantity,
      'Unit Price': item.UnitPrice ? `$${item.UnitPrice.toFixed(2)}` : 'N/A',
      'Extended Price': item.ExtendedPrice ? `$${item.ExtendedPrice.toFixed(2)}` : 'N/A',
      'Available Stock': item.QuantityAvailable,
      'Status': item.stockStatus.replace('-', ' ').toUpperCase()
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Query Results");
    
    const timestamp = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(wb, `bulk_query_results_${timestamp}.xlsx`);
    showNotification('Results exported successfully', 'success');
  };

  const handleAddAvailableToCart = () => {
    if (!orderingEnabled) {
      showNotification(orderingMessage, 'error');
      return;
    }
    const availableItems = uploadedData.filter(item => 
      item.isFound && item.stockStatus === 'in-stock'
    );

    if (availableItems.length === 0) {
      showNotification('No items in stock to add to cart', 'warning');
      return;
    }

    const cartItems = availableItems.map(item => ({
      partNumber: item.PartNumber,
      description: item.Description,
      quantity: Math.min(item.Quantity, item.QuantityAvailable),
      price: item.UnitPrice,
    }));

    const existingCart = JSON.parse(localStorage.getItem('cart') || '[]');
    const updatedCart = [...existingCart, ...cartItems];
    localStorage.setItem('cart', JSON.stringify(updatedCart));
    notifyCartUpdated();
    
    showNotification(`Added ${cartItems.length} items to cart`, 'success');
  };

  const handleCreateInquiry = () => {
    const outOfStockItems = uploadedData.filter(item => 
      item.stockStatus === 'out-of-stock' || item.stockStatus === 'low-stock'
    );

    if (outOfStockItems.length === 0) {
      showNotification('No out-of-stock items to inquire about', 'info');
      return;
    }

    // Store items for inquiry and redirect
    sessionStorage.setItem('bulkInquiryItems', JSON.stringify(outOfStockItems));
    history.push('/user/inquiry-lists?action=create-from-bulk');
  };

  const handleNewQuery = () => {
    setFile(null);
    setUploadedData([]);
    setIsReviewing(false);
    setFilterStatus('all');
  };

  const downloadTemplate = (format) => {
    const data = [
      { PartNumber: 'ABC123', Quantity: 10 },
      { PartNumber: 'XYZ789', Quantity: 5 },
      { CustomerMaterialNumber: 'CUST001', Quantity: 15 }
    ];

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Bulk Query");

    if (format === 'xlsx') {
      XLSX.writeFile(wb, "bulk_query_template.xlsx");
    } else if (format === 'csv') {
      XLSX.writeFile(wb, "bulk_query_template.csv", { bookType: "csv" });
    }
  };

  const summary = isReviewing ? calculateSummary() : null;
  const filteredData = isReviewing ? getFilteredData() : [];

  const summaryTiles = summary ? [
    { label: 'Total Items', value: summary.total, icon: Package, tone: 'neutral' },
    { label: 'In Stock', value: summary.inStock, icon: CheckCircle, tone: 'success' },
    { label: 'Low Stock', value: summary.lowStock, icon: AlertTriangle, tone: 'warning' },
    { label: 'Out of Stock', value: summary.outOfStock, icon: XCircle, tone: 'danger' },
    { label: 'Total Value', value: formatCurrency(summary.totalValue), icon: DollarSign, tone: 'neutral' },
  ] : [];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Bulk Query"
        description="Upload a list with Part Numbers or Customer Material Numbers and the required quantities to see their current availability and prices at a glance."
      />

      {!isReviewing ? (
        <>
          <Card header={<h2 className="text-lg font-semibold text-gray-900">Upload Your Query File</h2>}>
            <div
              className={`border-2 border-dashed rounded-lg p-10 text-center transition-colors duration-200 ${
                dragging ? 'border-brand bg-brand/10' : 'border-gray-300 hover:border-gray-400'
              }`}
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
            >
              <input
                type="file"
                id="file-upload"
                className="hidden"
                onChange={handleFileChange}
                accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
              />
              <label
                htmlFor="file-upload"
                className="cursor-pointer flex flex-col items-center justify-center"
              >
                <Upload className="w-12 h-12 text-gray-400 mb-4" />
                <p className="text-lg font-semibold text-gray-700 mb-2">
                  {file ? file.name : 'Drag and drop your file here'}
                </p>
                <p className="text-sm text-gray-500">
                  {file ? 'Click to change file' : 'or click to select a CSV or Excel file'}
                </p>
              </label>
            </div>

            {file && (
              <div className="flex items-center justify-between bg-gray-50 p-4 rounded-lg mt-4">
                <div className="flex items-center space-x-3">
                  <Package className="w-5 h-5 text-gray-600" />
                  <span className="text-sm font-medium text-gray-700">{file.name}</span>
                  <span className="text-xs text-gray-500">({(file.size / 1024).toFixed(1)} KB)</span>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setFile(null)}
                  ariaLabel="Remove file"
                  icon={<X className="w-4 h-4" aria-hidden="true" />}
                />
              </div>
            )}

            <div className="mt-6">
              <Button
                size="lg"
                onClick={handleUpload}
                disabled={!file}
                loading={loading}
                className="w-full sm:w-auto font-semibold"
                icon={<FileText className="w-5 h-5" aria-hidden="true" />}
              >
                {loading ? 'Processing...' : 'Process Query'}
              </Button>
            </div>
          </Card>

          <Card header={<h2 className="text-lg font-semibold text-gray-900">Download Templates</h2>}>
            <p className="text-sm text-gray-600 mb-4">Use these templates to ensure your bulk query is formatted correctly:</p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                variant="secondary"
                onClick={() => downloadTemplate('xlsx')}
                icon={<Download className="w-4 h-4" aria-hidden="true" />}
              >
                Excel Template
              </Button>
              <Button
                variant="secondary"
                onClick={() => downloadTemplate('csv')}
                icon={<Download className="w-4 h-4" aria-hidden="true" />}
              >
                CSV Template
              </Button>
            </div>
          </Card>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
            <div className="flex items-start">
              <AlertCircle className="flex-shrink-0 h-5 w-5 text-blue-600 mt-0.5" />
              <div className="ml-3">
                <h3 className="text-sm font-medium text-blue-800">Important Notes:</h3>
                <div className="mt-2 text-sm text-blue-700">
                  <ul className="list-disc pl-5 space-y-1">
                    <li>File must be in CSV or Excel format</li>
                    <li>Required column: PartNumber OR CustomerMaterialNumber</li>
                    <li>Optional column: Quantity (defaults to 1 if not provided)</li>
                    <li>Results show current availability and pricing</li>
                    <li>Export your results for record keeping</li>
                    <li>Add in-stock items directly to your cart</li>
                    <li>Create inquiries for out-of-stock or low-stock items</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </>
      ) : (
        <>
          {/* Summary Statistics */}
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
            {summaryTiles.map((tile) => {
              const Icon = tile.icon;
              return (
                <div key={tile.label} className="bg-white p-5 rounded-lg shadow-sm border border-gray-200">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium text-gray-600">{tile.label}</p>
                    <span className={`w-8 h-8 rounded-md flex items-center justify-center ${TILE_CHIP[tile.tone]}`}>
                      <Icon className="w-4 h-4" aria-hidden="true" />
                    </span>
                  </div>
                  <p className="text-2xl font-bold text-gray-900 mt-2 tabular-nums">{tile.value}</p>
                </div>
              );
            })}
          </div>

          {/* Action Bar */}
          <Card padding="sm">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center space-x-2">
                <Filter className="w-5 h-5 text-gray-500" aria-hidden="true" />
                <label htmlFor="stock-filter" className="sr-only">Filter by stock status</label>
                <select
                  id="stock-filter"
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent text-sm"
                >
                  <option value="all">All Items ({summary.total})</option>
                  <option value="in-stock">In Stock ({summary.inStock})</option>
                  <option value="low-stock">Low Stock ({summary.lowStock})</option>
                  <option value="out-of-stock">Out of Stock ({summary.outOfStock})</option>
                  <option value="not-found">Not Found ({summary.notFound})</option>
                </select>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  variant="secondary"
                  onClick={handleExportResults}
                  icon={<Download className="w-4 h-4" aria-hidden="true" />}
                >
                  Export Results
                </Button>
                <Button
                  variant="secondary"
                  onClick={handleCreateInquiry}
                  disabled={summary.outOfStock === 0 && summary.lowStock === 0}
                  icon={<MessageSquare className="w-4 h-4" aria-hidden="true" />}
                >
                  Create Inquiry
                </Button>
                <Button
                  variant="secondary"
                  onClick={handleNewQuery}
                  icon={<RefreshCw className="w-4 h-4" aria-hidden="true" />}
                >
                  New Query
                </Button>
                {orderingEnabled && (
                  <Button
                    onClick={handleAddAvailableToCart}
                    disabled={summary.inStock === 0}
                    icon={<ShoppingCart className="w-4 h-4" aria-hidden="true" />}
                  >
                    Add Available to Cart
                  </Button>
                )}
              </div>
            </div>
          </Card>

          {/* Results Table */}
          <Card padding="none" className="overflow-hidden">
            {filteredData.length === 0 ? (
              <EmptyState
                icon={<Filter className="h-12 w-12" />}
                title="No items match this filter"
                description="Choose a different stock status to see more results."
              />
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Image</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Part Number</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Qty</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Unit Price</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ext. Price</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Available</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredData.map((item, index) => (
                      <tr key={index} className="hover:bg-gray-50 transition-colors duration-150">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <img
                            src={item.SmallImageUrl}
                            alt={item.PartNumber}
                            className="w-12 h-12 object-contain"
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = placeholderImageUrl;
                            }}
                          />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{item.PartNumber}</div>
                          {item.isAlternative && (
                            <div className="text-xs text-orange-600">Alt: {item.PrimaryPartNumber}</div>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-900">{item.Description}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 tabular-nums">
                          {item.Quantity}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 tabular-nums">
                          {item.UnitPrice > 0 ? formatCurrency(item.UnitPrice) : 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 tabular-nums">
                          {item.ExtendedPrice > 0 ? formatCurrency(item.ExtendedPrice) : 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 tabular-nums">
                          {item.QuantityAvailable}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {getStatusBadge(item.stockStatus)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </>
      )}

      <AnimatePresence>
        {isSearching && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <LoadingOverlay message="Processing query..." />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default BulkQuery;
